import React, { useEffect, useMemo, useState } from 'react';
import { Button } from './ui';
import { wellnessAreaService, userWellnessService } from '../services/api';

const CATEGORY_LABELS = {
  mood: 'Mood & Affect',
  trauma: 'Trauma',
  relationship: 'Relationships',
  substance: 'Substance Use',
  grief: 'Grief & Loss',
  stress: 'Stress & Overwhelm',
  identity: 'Identity & Purpose',
  behavioral: 'Behavior & Regulation',
};

const CATEGORY_ORDER = [
  'mood',
  'trauma',
  'relationship',
  'substance',
  'grief',
  'stress',
  'identity',
  'behavioral',
];

const MAX_SELECTIONS = 5;

const WellnessAreaSelector = ({
  userId,
  currentAreas = [],
  readOnly = false,
  setBy = 'unset',
  onSave,
}) => {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() =>
    (currentAreas || []).map((a) => (typeof a === 'string' ? a : a._id))
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [warning, setWarning] = useState(null);

  const clinicianLocked = setBy === 'clinician';
  const disabledAll = clinicianLocked || readOnly;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await wellnessAreaService.list();
        if (cancelled) return;
        const data = response?.data?.data || response?.data || [];
        setAreas(Array.isArray(data) ? data : []);
      } catch (err) {
        if (cancelled) return;
        setError(
          err?.response?.data?.message ||
            err?.message ||
            'Unable to load wellness areas.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setSelected((currentAreas || []).map((a) => (typeof a === 'string' ? a : a._id)));
  }, [currentAreas]);

  const grouped = useMemo(() => {
    const map = {};
    for (const area of areas) {
      const cat = area.clinicalCategory || 'other';
      if (!map[cat]) map[cat] = [];
      map[cat].push(area);
    }
    return map;
  }, [areas]);

  const orderedCategories = useMemo(() => {
    const present = Object.keys(grouped);
    const ordered = CATEGORY_ORDER.filter((c) => present.includes(c));
    const extras = present.filter((c) => !CATEGORY_ORDER.includes(c));
    return [...ordered, ...extras];
  }, [grouped]);

  const toggle = (areaId) => {
    if (disabledAll) return;
    setWarning(null);
    setSaveError(null);
    setSuccessMessage(null);
    setSelected((prev) => {
      if (prev.includes(areaId)) {
        return prev.filter((id) => id !== areaId);
      }
      if (prev.length >= MAX_SELECTIONS) {
        setWarning(`You can select up to ${MAX_SELECTIONS} focus areas.`);
        return prev;
      }
      return [...prev, areaId];
    });
  };

  const handleKeyDown = (event, areaId) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggle(areaId);
    }
  };

  const handleSubmit = async () => {
    if (disabledAll) return;
    try {
      setSaving(true);
      setSaveError(null);
      setSuccessMessage(null);
      const response = await userWellnessService.updateAreas(userId, selected);
      const saved = response?.data?.data ?? response?.data;
      setSuccessMessage('Focus areas saved.');
      if (onSave) onSave(saved);
    } catch (err) {
      setSaveError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to save focus areas. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-textSecondary">Loading wellness areas...</div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-error/10 border border-error/20 rounded-lg text-error">
        {error}
      </div>
    );
  }

  const atMax = selected.length >= MAX_SELECTIONS;

  return (
    <div className="space-y-6">
      {clinicianLocked && (
        <div className="p-4 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-900">
          Your care team has identified these focus areas for you. Contact your
          clinician to make changes.
        </div>
      )}

      {!clinicianLocked && (
        <div className="flex items-center justify-between">
          <p className="text-textSecondary">
            Select up to {MAX_SELECTIONS} areas that matter most to you right now.
          </p>
          <span className="text-sm text-textMuted">
            {selected.length} / {MAX_SELECTIONS} selected
          </span>
        </div>
      )}

      {warning && (
        <div className="p-3 rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-sm">
          {warning}
        </div>
      )}

      {saveError && (
        <div className="p-3 rounded-md border border-error/20 bg-error/10 text-error text-sm">
          {saveError}
        </div>
      )}

      {successMessage && (
        <div className="p-3 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm">
          {successMessage}
        </div>
      )}

      <div className="space-y-8">
        {orderedCategories.map((category) => (
          <div key={category}>
            <h3 className="text-lg font-semibold text-textPrimary mb-3">
              {CATEGORY_LABELS[category] || category}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {grouped[category].map((area) => {
                const isSelected = selected.includes(area._id);
                const disabled =
                  disabledAll || (!isSelected && atMax);

                const baseClasses =
                  'rounded-lg border p-4 transition-all duration-200 outline-none';
                const stateClasses = isSelected
                  ? 'border-emerald-500 bg-emerald-50 shadow-sm'
                  : 'border-border bg-surface hover:border-emerald-300';
                const disabledClasses = disabled
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer focus:ring-2 focus:ring-emerald-400';

                return (
                  <div
                    key={area._id}
                    role="button"
                    tabIndex={disabled ? -1 : 0}
                    aria-pressed={isSelected}
                    aria-disabled={disabled}
                    onClick={() => !disabled && toggle(area._id)}
                    onKeyDown={(e) => !disabled && handleKeyDown(e, area._id)}
                    className={`${baseClasses} ${stateClasses} ${disabledClasses}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-semibold text-textPrimary">
                        {area.displayName}
                      </h4>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full border ${
                          isSelected
                            ? 'bg-emerald-500 text-white border-emerald-500'
                            : 'bg-bgSecondary text-textMuted border-border'
                        }`}
                      >
                        {isSelected ? 'Selected' : 'Select'}
                      </span>
                    </div>
                    {area.description && (
                      <p className="mt-2 text-sm text-textSecondary">
                        {area.description}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {!disabledAll && (
        <div className="flex items-center gap-3 pt-4 border-t border-border">
          <Button
            variant="primary"
            size="md"
            onClick={handleSubmit}
            disabled={saving || selected.length === 0}
          >
            {saving ? 'Saving...' : 'Save focus areas'}
          </Button>
          {selected.length === 0 && (
            <span className="text-sm text-textMuted">
              Pick at least one area to save.
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default WellnessAreaSelector;
